import { Dropdown } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { IoMdRefresh } from "react-icons/io";
import { useEffect, useMemo, useState } from "react";
import { useContext } from "react";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import StatusBadge from "../../generals/StatusBadge";
import { departmentService } from "../../services/department.service";
import {
  GetDepartmentList,
  SetDepartmentList,
} from "../../store/departmentAtom";
import DepartmentFormModal from "./DepartmentFormModal";
import "./DepartmentSetup.css";

function DepartmentListPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const setDepartmentList = SetDepartmentList();
  const departmentList = GetDepartmentList();
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState("");

  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3] || "departments";
  const basePath = `/${currModuleId}/${currModuleName}/${currUserMenu}`;

  const loadDepartments = () => {
    setLoading(true);
    setError("");

    return departmentService
      .getDepartments()
      .then((res) => {
        setDepartmentList({
          departments: res.departments,
          raw: res.raw,
          loaded: true,
        });
      })
      .catch((err) => {
        if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
          setError("Unable to load departments. Please try again.");
        }
      })
      .finally(() => setLoading(false));
  };

  async function handleCreateDepartment({ name }) {
    setIsCreating(true);
    try {
      const result = await departmentService.createDepartment(name);
      logicContext.showToast(result.message, "success");
      await loadDepartments();
      return result;
    } finally {
      setIsCreating(false);
    }
  }

  useEffect(() => {
    document.title = "Department Setup";
    if (!departmentList.loaded) {
      loadDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredDepartments = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return departmentList.departments.filter((department) => {
      const matchesSearch = String(department.name ?? "").toLowerCase().includes(search);
      const matchesStatus =
        statusFilter === "All" || department.statusLabel === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [departmentList.departments, searchText, statusFilter]);

  const columns = [
    {
      title: "Id",
      key: "id",
      dataIndex: "id",
      sorter: (a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0),
    },
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      className: "text-primary",
      sorter: (a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""),
      render: (record, row) => (
        <Link
          className="text-primary"
          to={`${basePath}/${row.id}`}
          onClick={(event) => event.stopPropagation()}
        >
          {record}
        </Link>
      ),
    },
    {
      title: "Status",
      key: "statusLabel",
      dataIndex: "statusLabel",
      sorter: (a, b) => (a?.statusLabel ?? "").localeCompare(b?.statusLabel ?? ""),
      render: (record, row) => (
        <StatusBadge statusId={row.isActive ? 1 : "I"} statusName={record} />
      ),
    },
    {
      title: "Created By",
      key: "createdBy",
      dataIndex: "createdBy",
      sorter: (a, b) => (a?.createdBy ?? "").localeCompare(b?.createdBy ?? ""),
    },
    {
      title: "Created At",
      key: "createdAt",
      dataIndex: "createdAt",
      sorter: (a, b) => (a?.createdAtRaw ?? "").localeCompare(b?.createdAtRaw ?? ""),
    },
  ];

  function handleRowClick(department) {
    navigate(`${basePath}/${department.id}`);
  }

  return (
    <div className="@container">
      <div className="@xl:flex @xl:justify-between">
        <div className="mt-4 flex flex-wrap gap-2">
          <div className="flex rounded bg-filterColor">
            <Dropdown>
              <Dropdown.Toggle
                style={{
                  backgroundColor: "#E5E5E5",
                  border: "none",
                  color: "#45444B",
                  fontSize: "12px",
                  height: "35px",
                }}
                id="department-filter-dropdown"
              >
                FILTER BY
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setStatusFilter("All")}>
                  All
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Active")}>
                  Active
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setStatusFilter("Inactive")}>
                  Inactive
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <Tooltip title="Reload">
            <button
              type="button"
              onClick={loadDepartments}
              disabled={loading || isCreating}
              className="group flex h-[35px] items-center justify-center px-2 border-[1px] border-gray-200 bg-white rounded-md hover:bg-gray-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            >
              <IoMdRefresh className="h-6 w-6 text-gray-600 group-active:rotate-45 transition-all duration-300" />
            </button>
          </Tooltip>

          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-[35px] w-full @sm:w-[260px] rounded-md border border-gray-200 px-3 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Search by name"
          />
        </div>

        <div className="flex items-center">
          {selectedDepartments.length > 0 ? (
            <div className="mt-4 mr-2 text-sm text-gray-600">
              {selectedDepartments.length} selected
            </div>
          ) : null}

          <button
            onClick={() => setShowCreateModal(true)}
            disabled={loading || isCreating}
            className="mt-4 py-2.5 px-6 text-sm rounded-md bg-blue-500 text-white font-medium focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          >
            Add New
          </button>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="department-skeleton h-8 rounded" />
            <div className="department-skeleton mt-3 h-12 rounded" />
            <div className="department-skeleton mt-3 h-12 rounded" />
            <div className="department-skeleton mt-3 h-12 rounded" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {!loading && !error ? (
          <GeneralCustomTable
            tableHeadList={columns}
            tableDataList={filteredDepartments}
            allowCheckBox={false}
            rowKey={(record) => record.id}
            handleRowClick={handleRowClick}
            pageSizeOptions={["10", "25", "50"]}
          />
        ) : null}
      </div>

      {showCreateModal ? (
        <DepartmentFormModal
          mode="create"
          initialValues={{ name: "", statusId: 2 }}
          onSubmit={handleCreateDepartment}
          onClose={() => setShowCreateModal(false)}
        />
      ) : null}
    </div>
  );
}

export default DepartmentListPage;
